import * as Sentry from "@sentry/react";
import { createContext, useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Statuses from "../constants/Statuses";
import { checkIfInIframe } from "../utils/checkIfInIframe";
import apiSlice from "../utils/store-slices/apiSlice";
import {
	useGetCurrentUserQuery,
	useLoginAsUserMutation,
	useLoginMutation,
	useLoginViaTokenMutation,
	useLogoutMutation,
} from "../utils/store-slices/authApiSlice";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
	const dispatch = useDispatch();
	const isInIframe = checkIfInIframe();
	const [login] = useLoginMutation();
	const [loginViaToken] = useLoginViaTokenMutation();
	const [loginAsUser, { isLoading: isSigningInAsUser }] =
		useLoginAsUserMutation();
	const [logout] = useLogoutMutation();

	const [user, setUser] = useState(null);
	const [accounts, setAccounts] = useState(null);

	const [synchroStatus, setSynchroStatus] = useState(Statuses.IDLE);

	const [loggedIn, setLoggedIn] = useState(null);

	const [isRefetchingUser, setIsRefetchingUser] = useState(false);
	const {
		refetch,
		isLoading: isLoadingUser,
		isFetching: isFetchingUser,
	} = useGetCurrentUserQuery(null, {
		skip: loggedIn !== null,
	});

	const setUserEmail = (email) => {
		setUser((prevState) => ({ ...prevState, email }));
	};

	const addNewAccount = (account) => {
		setAccounts((prevaccounts) => [...prevaccounts, account]);
	};

	const removeAccount = (account) => {
		setAccounts((prevaccounts) =>
			prevaccounts.filter((item) => item.id !== account.id),
		);
	};

	const editAccounts = (accounts) => {
		setAccounts((prevaccounts) => {
			return prevaccounts.map((item) => {
				if (accounts[item.id]) {
					return { ...item, ...accounts[item.id] };
				}
				return item;
			});
		});
	};

	useEffect(() => {
		const fetchUserData = async () => {
			try {
				setIsRefetchingUser(true);
				const refetchPromise = await refetch();
				if (refetchPromise.status === "fulfilled") {
					setUser(refetchPromise.data.user);
					setAccounts(refetchPromise.data.stores);
					setLoggedIn(true);
					setIsRefetchingUser(false);
				} else {
					setLoggedIn(false);
					setIsRefetchingUser(false);
				}
			} catch {
				setLoggedIn(false);
				setIsRefetchingUser(false);
			}
		};

		if (loggedIn === null && !isInIframe) {
			fetchUserData();
		}
	}, [loggedIn, refetch, isInIframe]);

	const signIn = async (email, password) => {
		try {
			const data = await login({ email, password }).unwrap();
			setUser(data.user);
			setAccounts(data.stores);
			setLoggedIn(true);
		} catch (error) {
			console.error(error);

			if (
				error.status === "FETCH_ERROR" ||
				error.status === 500 ||
				error.status >= 500
			) {
				return "Chwilowy brak dostępu do usługi, proszę spróbować później";
			}

			if (
				typeof error.status != "undefined" &&
				error.status == 400 &&
				typeof error.data != "undefined" &&
				typeof error.data.Message != "undefined"
			) {
				return error.data.Message;
			} else {
				throw new Error("Error during sign in:", error);
			}
		}
	};

	const signInWithToken = async (token) => {
		try {
			const data = await loginViaToken({ token }).unwrap();
			setUser(data.user);
			setAccounts(data.stores);
			setLoggedIn(true);

			return data;
		} catch (error) {
			console.error("Error during sign in with token:", error);
			throw new Error("Error during sign in with token:", error);
		}
	};

	const signInAsUser = async (id, token, name) => {
		try {
			const data = await loginAsUser({ id, token, name }).unwrap();
			dispatch(apiSlice.util.resetApiState());
			setUser(data.user);
			setAccounts(data.stores);
			setLoggedIn(true);

			return data;
		} catch (error) {
			console.error("AuthContext: Error during sign in with token:", error);
			throw new Error("Error during sign in with token:", error);
		}
	};

	const signOut = async () => {
		try {
			// wait for logout to resolve or throw
			await logout().unwrap();

			// only when logout succeeds:
			dispatch(apiSlice.util.resetApiState());
			setUser(null);
			setAccounts(null);
			setLoggedIn(false);
		} catch (error) {
			console.error("Error during sign out:", error);

			// Error normalization:
			const normalized = (() => {
				if (error && typeof error === "object") {
					const { status, data, error: errMsg, originalStatus } = error;
					return {
						message: errMsg || error.message || String(error),
						status,
						data,
						originalStatus,
						raw: error,
					};
				}
				return { message: String(error), raw: error };
			})();

			// Attach context/extra so Sentry gets structured data and stack if present
			Sentry.setContext("signOutError", normalized);
			// Capture the real Error if possible, otherwise capture a message
			if (error instanceof Error) {
				Sentry.captureException(error);
			} else {
				// captureMessage will produce a clear event, but you already set context
				Sentry.captureMessage(`Sign out failed: ${normalized.message}`);
			}
		}
	};

	const value = {
		synchroStatus,
		setSynchroStatus,
		signIn,
		signOut,
		user,
		addNewAccount,
		removeAccount,
		accounts,
		loggedIn,
		editAccounts,
		setUserEmail,
		signInWithToken,
		signInAsUser,
		isSigningInAsUser,
		isLoadingUser,
		isFetchingUser,
		isRefetchingUser,
		setUser,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
